import { cn } from '@/util/cn';
import usePlaygroundDemoRole, {
  type PlaygroundDemoRole
} from '@/util/hooks/usePlaygroundDemoRole';
import { useLocation, useNavigate } from 'react-router';

import { OnboardingSectionPaths } from './SupplierOnboardingRequest/Stepper/sections/onboardingSectionPaths';
import usePlaygroundDemoChromeVisible from './usePlaygroundDemoChromeVisible';

const SENT = OnboardingSectionPaths.SupplierOnboardingDashboardRequestsSent as string;
const RECEIVED =
  OnboardingSectionPaths.SupplierOnboardingDashboardRequestsReceived as string;

/**
 * Where a role change should take the current page: the design prototype's setRoleClient /
 * setRoleSupplier switch the Network tab (suppliers ↔ clients) and the onboarding dashboard
 * tab (sent ↔ received). Any other page stays put.
 */
const targetForRole = (pathname: string, role: PlaygroundDemoRole): string | null => {
  if (pathname.startsWith('/network/')) {
    return role === 'client' ? '/network/suppliers' : '/network/clients';
  }
  if (pathname === SENT || pathname.startsWith(`${SENT}/`)) {
    return role === 'client' ? SENT : RECEIVED;
  }
  return null;
};

const OPTIONS: ReadonlyArray<{ role: PlaygroundDemoRole; label: string }> = [
  { role: 'client', label: "I'm the Client" },
  { role: 'supplier', label: "I'm the Supplier" }
];

/**
 * "I'm the Client / I'm the Supplier" bar under the Playground banner (#9141). The design
 * prototype labels this "Demo-only role switcher — not a real B1Link control", so it renders
 * only while Playground is ON (user decision, 2026-10-20). It picks which side of the
 * marketplace the demo data is presented from.
 */
const PlaygroundRoleSwitcher = () => {
  const visible = usePlaygroundDemoChromeVisible();
  const [role, setRole] = usePlaygroundDemoRole();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (!visible) {
    return null;
  }

  const choose = (next: PlaygroundDemoRole) => {
    setRole(next);
    const target = targetForRole(pathname, next);
    if (target && target !== pathname) {
      navigate(target);
    }
  };

  return (
    <div
      data-testid="playground-demo-chrome"
      className="flex items-center justify-center gap-3 border-b border-slate-200 bg-white py-[9px]"
    >
      <div className="flex rounded-full bg-[#efeff0] p-[3px]">
        {OPTIONS.map((option) => {
          const selected = option.role === role;
          return (
            <button
              key={option.role}
              type="button"
              aria-pressed={selected}
              onClick={() => choose(option.role)}
              className={cn(
                'cursor-pointer rounded-full px-[18px] py-[5px] text-[12.5px] font-bold',
                selected
                  ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(0,0,0,0.1)]'
                  : 'text-[#64748b]'
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PlaygroundRoleSwitcher;
